/** @format */
import axios from 'axios';

import {
    ContentfulQueryId,
    pageDataSerializer,
} from '@toucan/common/contentful';
import { normalizeIsoCode } from '@toucan/common/helpers/language';

import { LegalLinksContextProps } from '@toucan/client/global/application';
import { MarketCode } from '@toucan/client/global/market';

import {
    GetHomepageDataResponse,
    PageData,
    getCategoryCardsResponse,
} from '@toucan/client/core/contentful';

import { SiteSection } from '../config/constants';
import { CmsBasicPageData, QueryVariables } from '../config/types';

export async function getPageData<V = QueryVariables>({
    locale,
    queryId = ContentfulQueryId.GENERAL_PAGE_DATA,
    siteSection,
    ...variables
}: V & QueryVariables): Promise<PageData> {
    const normalizedLocale = normalizeIsoCode(locale);

    const response = await axios.post('/cms/contentful', {
        queryId,
        variables: {
            ...variables,
            siteSection,
            limit: siteSection.length,
            locale: normalizedLocale,
            baseLocale: 'en-GB',
        },
        locale: normalizedLocale,
    });

    return pageDataSerializer(response);
}

/**
 * Gets data required to render the homepage from contentful
 * @param {string} locale ISO locale code
 */
export async function getHomepageData(
    locale: string
): Promise<GetHomepageDataResponse> {
    const normalizedLocale = normalizeIsoCode(locale);

    const { data } = await axios.post('/cms/contentful', {
        queryId: ContentfulQueryId.HOMEPAGE_DATA,
        variables: {
            locale: normalizedLocale,
            siteSection: [SiteSection.HOMEPAGE],
        },
        locale: normalizedLocale,
    });

    return data?.data;
}

/**
 * Gets data required to render the category cards from contentful
 * @param {string} locale ISO locale code
 */
export async function getCategoryCards(
    locale: string
): Promise<getCategoryCardsResponse> {
    const normalizedLocale = normalizeIsoCode(locale);

    const { data } = await axios.post('/cms/contentful', {
        queryId: ContentfulQueryId.CONTENT_HUB_CATEGORIES,
        variables: {
            locale: normalizedLocale,
        },
        locale: normalizedLocale,
    });

    const results = data?.data?.contentCategoryCollection?.items ?? [];
    const total = data?.data?.contentCategoryCollection?.total ?? -1;

    return {
        results,
        total,
    };
}

export async function getBasicPage(
    slug: string,
    locale: string,
    market: MarketCode
): Promise<CmsBasicPageData> {
    const normalizedLocale = normalizeIsoCode(locale);

    const { data } = await axios.post('/cms/contentful', {
        queryId: ContentfulQueryId.BASIC_PAGE_BY_SLUG,
        variables: {
            locale: normalizedLocale,
            market,
            slug: decodeURI(slug),
        },
        locale: normalizedLocale,
    });

    return data?.data?.pageHelpCollection?.items[0];
}

export async function getArticles(
    numberToFetch: number,
    numberToSkip: number,
    locale: string,
    market: MarketCode,
    tag?: string
) {
    const normalizedLocale = normalizeIsoCode(locale);

    const { data } = await axios.post('/cms/contentful', {
        queryId: tag
            ? ContentfulQueryId.PAGINATED_ARTICLES_BY_CATEGORY
            : ContentfulQueryId.PAGINATED_ARTICLES,
        variables: {
            limit: numberToFetch,
            skip: numberToSkip,
            locale: normalizedLocale,
            slug: tag,
            baseLocale: 'en-GB',
            market,
        },
        locale: normalizedLocale,
    });

    const results = data?.data?.articleCollection?.items ?? [];
    const total = data?.data?.articleCollection?.total ?? -1;

    return {
        results,
        total,
    };
}

export async function getLegalPageNavigation(
    market: MarketCode,
    locale: string
): Promise<LegalLinksContextProps | undefined> {
    const normalizedLocale = normalizeIsoCode(locale);

    // Wrapped in a try catch due to being called when we get the other navigation data (brand & category)
    // and we don't want to those to fail if this call fails. Otherwise we lose the other navigation data.
    try {
        const { data } = await axios.post('/cms/contentful', {
            queryId: ContentfulQueryId.LEGAL_PAGES_NAVIGATION,
            variables: {
                locale: normalizedLocale,
                market,
            },
            locale: normalizedLocale,
        });

        const legalPageData = data?.data as { items: [{ urlSlug: string }] }[];

        if (!legalPageData) {
            return;
        }

        return Object.entries(legalPageData).reduce((legal, [key, data]) => {
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            legal[key] = data.items[0];
            return legal;
        }, {});
    } catch (error) {
        console.error(error);
        return {};
    }
}
